import { IonHeader, IonList, IonPage, IonTitle, useIonViewWillEnter } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { DeviceListItem } from "@/components/molecules/notifications/device-list-item";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { Device } from "@/lib/capacitor/device";
import { queryClient } from "@/lib/providers/query";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

export function NotificationsDevicesPage() {
  const { t } = useTranslation();
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);

  const { data: userDevices, refetch: refetchDevices } = useQuery({
    queryKey: ["notification", "devices"],
    queryFn: api.notification.getDevices,
    enabled: Capacitor.getPlatform() !== "web",
  });

  // Get current device identifier on mount
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") {
      return;
    }

    Device.getDeviceIdentifier().then(setCurrentDeviceId);
  }, []);

  useIonViewWillEnter(() => {
    refetchDevices();
  });

  const removeDeviceMutation = useMutation({
    mutationKey: ["notification", "remove-device"],
    mutationFn: async (deviceIdentifier: string) => {
      await api.notification.deleteDevice(deviceIdentifier);
    },
    onSuccess: () => {
      toast.success(t("settings.notifications.other-devices.manage.remove.success"));
      queryClient.invalidateQueries({ queryKey: ["notification", "devices"] });
    },
    onError: (error) => {
      toast.error(t("settings.notifications.other-devices.manage.remove.error"));
      logger.error({ error }, "Failed to remove device");
    },
  });

  const devices = userDevices?.data?.devices || [];
  const hasDevices = devices.length > 0;

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <Buttons slot="secondary">
            <BackButton defaultHref={Routes.Notifications()} />
          </Buttons>
          <IonTitle>{t("routes:notifications.devices.title")}</IonTitle>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome>
        {hasDevices ? (
          <div className="flex flex-1 flex-col pt-4">
            {/* Description */}
            <div className="mx-4 mb-4">
              <p className="text-sm text-txt-secondary">
                {t("settings.notifications.other-devices.manage.description")}
              </p>
            </div>

            {/* Device List */}
            <IonList lines="full">
              {devices.map((device) => (
                <DeviceListItem
                  key={device.id}
                  device={device}
                  isCurrentDevice={device.deviceIdentifier === currentDeviceId}
                  onRemove={removeDeviceMutation.mutateAsync}
                />
              ))}
            </IonList>
          </div>
        ) : (
          // Empty State
          <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6">
            <div className="flex size-24 items-center justify-center rounded-full bg-neutral-50">
              <svg
                className="size-12 text-txt-tertiary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>

            <div className="text-center">
              <h2 className="text-xl font-semibold text-txt-primary">
                {t("settings.notifications.other-devices.manage.empty.title")}
              </h2>
              <p className="mt-2 text-sm text-txt-secondary">
                {t("settings.notifications.other-devices.manage.empty.description")}
              </p>
            </div>

            <Button routerLink={Routes.Notifications()} expand="block" className="w-full max-w-sm">
              {t("settings.notifications.other-devices.manage.empty.action")}
            </Button>
          </div>
        )}
      </Content>
    </IonPage>
  );
}
